//import { Table } from "react-bootstrap";
import React from "react";
import { useCollection } from '@cloudscape-design/collection-hooks';
import { Table, PropertyFilter, Header, Button, Pagination } from '@cloudscape-design/components';
import { Formatter } from "../format";
import { SentimentIcon } from "./SentimentIcon";
import { TrendIcon } from "./TrendIcon";
import "./ContactTable.css";
import { DateTimeForm, formatDateTime } from './DateTimeForm';
import { ContactTablePreferences, DEFAULT_PREFERENCES } from './ContactTablePreferences'
import { useLocalStorage } from '../common/localStorage';
import Popover from "@cloudscape-design/components/popover";
import Link from "@cloudscape-design/components/link";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { resolveCalls } from "../api/api";
import { payloadFromToken } from "../api/auth";

const getQualityScoreDotClass = (value) => {
  const score = Number(value);
  if (!Number.isFinite(score)) return "status-dot--grey";
  if (score >= 7) return "status-dot--green";
  if (score >= 6) return "status-dot--yellow";
  if (score >= 4) return "status-dot--orange";
  if (score >= 1) return "status-dot--red";
  return "status-dot--grey";
};

const getQualityScoreSortValue = (value, isDescending) => {
  const score = Number(value);
  if (!Number.isFinite(score) || score === 0) {
    return isDescending ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
  }
  return score;
};

const BASE_COLUMN_DEFINITIONS = [
  {
    id: "timestamp",
    header: "Timestamp",
    sortingField: "timestamp",
    cell: (d) => {
      if (d.status !== undefined && d.status.length > 0 && d.status !== "Done") {
        return (
          Formatter.TimestampInTimeZone(d.timestamp, "America/Chicago")
        )
      }
      else {
        return (
          <Link
            href={`/dashboard/${d.key}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {Formatter.TimestampInTimeZone(d.timestamp, "America/Chicago")}
          </Link>
        )
      }
    },
    minWidth: 160
  },
  {
    id: "jobName",
    header: "Job Name",
    cell: (d) => {
      if (d.status !== undefined && d.status.length > 0 && d.status !== "Done") {
        return (
          d.jobName
        )
      }
      else {
        return (
          <Link
            variant="primary"
            href={`/dashboard/${d.key}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {d.jobName}
          </Link>
        )
      }
    },
    isRowHeader: true,
    sortingField: "jobName",
  },
  {
    id: "status",
    header: "Status",
    cell: (d) => d.status,
    isRowHeader: true,
    sortingField: "status",
    minWidth: 130
  },
  {
    id: "guid",
    header: "GUID",
    cell: (d) => d.guid,
    isRowHeader: true,
    sortingField: "guid",
    minWidth: 150
  },
  {
    id: "agent",
    header: "AGT Ext",
    cell: (d) => d.agent,
    isRowHeader: true,
    sortingField: "agent",
    minWidth: 114
  },
  {
    id: "employee",
    header: "AGT Name",
    cell: (d) => d.employee,
    isRowHeader: true,
    sortingField: "employee",
    minWidth: 156
  },
  {
    id: "customer",
    header: "Customer",
    cell: (d) => d.customer,
    isRowHeader: true,
    sortingField: "customer",
    minWidth: 130
  },
  {
    id: "queue",
    header: "Queue",
    cell: (d) => d.queue,
    isRowHeader: true,
    sortingField: "queue",
    minWidth: 130
  },
  {
    id: "state",
    header: "State",
    cell: (d) => d.state,
    isRowHeader: true,
    sortingField: "state",
    minWidth: 100
  },

  {
    id: "summary_topic",
    header: "Topic",
    cell: (d) => d.summary_topic,
    isRowHeader: true,
    sortingField: "summary_topic",
    minWidth: 160
  },
  {
    id: "summary_product",
    header: "Product",
    cell: (d) => d.summary_product,
    isRowHeader: true,
    sortingField: "summary_product",
    minWidth: 130
  },
  {
    id: "summary_summary",
    header: "Summary",
    cell: (d) => {
      console.log(d);
      if (d.summary_summary !== undefined) {
        return (
          <Popover
            dismissButton={false}
            position="top"
            size="large"
            triggerType="text"
            content={d.summary_summary}
          >
            {(d.summary_summary.length > 35 ? d.summary_summary.substring(0, 35) + "..." : d.summary_summary)}
          </Popover>
          /*
          <ExpandableSection headerText={(d.summary_summary.length > 50 ? d.summary_summary.substring(0,50) + "..." : d.summary_summary )}>
            {d.summary_summary}
          </ExpandableSection>*/
        )
      }
      return 'n/a';
    },
    isRowHeader: true,
    sortingField: "summary_summary",
    minWidth: 320
  },
  {
    id: "callerSentimentScore",
    header: "Cust Sent",
    sortingField: "callerSentimentScore",
    cell: (d) => (
      <div className="d-flex justify-content-evenly">
        <SentimentIcon score={d?.callerSentimentScore} />
        <TrendIcon trend={d.callerSentimentChange} />
      </div>
    ),
    minWidth: 130
  },
  {
    id: "qualityScore",
    header: "Score",
    cell: (d) => {
      const score = Number(d?.qualityScore);
      const hasScore = Number.isFinite(score) && score !== 0;
      const dotClass = getQualityScoreDotClass(score);
      return (
        <span className="score-cell">
          <span className={`status-dot ${dotClass}`} />
          <span>{hasScore ? score : "-"}</span>
        </span>
      );
    },
    minWidth: 100
  },
  {
    id: "langCode",
    header: <div className="col-header-wrapper text-left">Lang Code</div>,
    cell: (d) => d.lang,
    minWidth: 100,
  },
  {
    id: "duration",
    header: <div className="col-header-wrapper text-left">Duration</div>,
    sortingField: "duration",
    cell: (d) => Formatter.Time(d.duration),
    minWidth: 100,
  },
  {
    id: "summary_resolved",
    header: "Resolved",
    cell: (d) => {
      const value = d.summary_resolved;
      if (value === undefined || value === null) return "–";
      const stringValue = String(value).trim();
      return stringValue.length > 0 ? stringValue : "–";
    },
    isRowHeader: true,
    sortingField: "summary_resolved",
    minWidth: 130
  }
];

const getMatchesCountText = function getMatchesCountText(count) {
  return count === 1 ? `1 match` : `${count} matches`;
}

const COLUMN_WIDTHS_DEFAULTS = [
  { id: 'timestamp', width: 180 },
  { id: 'jobName', width: 150 },
  { id: 'status', width: 140 },
  { id: 'guid', width: 200 },
  { id: 'agent', width: 114 },
  { id: 'employee', width: 168 },
  { id: 'customer', width: 180 },
  { id: 'queue', width: 140 },
  { id: 'state', width: 100 },
  { id: 'summary_topic', width: 160 },
  { id: 'summary_product', width: 180 },
  { id: 'summary_summary', width: 320 },
  { id: 'callerSentimentScore', width: 180 },
  { id: 'qualityScore', width: 100 },
  { id: 'langCode', width: 120 },
  { id: 'duration', width: 112 },
  { id: 'summary_resolved', width: 140 },
];
const DEFAULT_COLUMN_WIDTH = 120;

export const ContactTable = ({
  data = [],
  loading = false,
  empty,
  header,
  variant = 'embedded',
  extraFilters = null,
  username: providedUsername = "",
  onResolved = () => { },
  hideSearchBar = false,
}) => {
  const [preferences, setPreferences] = useLocalStorage(
    'contact-table-preferences',
    DEFAULT_PREFERENCES,
  );
  const [columnWidths, setColumnWidths] = useLocalStorage(
    'contact-table-widths',
    COLUMN_WIDTHS_DEFAULTS,
  );
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [resolvedOverrides, setResolvedOverrides] = React.useState({});
  const [isResolving, setIsResolving] = React.useState(false);
  const [username, setUsername] = React.useState("");

  React.useEffect(() => {
    // try to get from local storage first (set by auth.js on login)
    const storedUsername = window.localStorage.getItem("username");
    if (storedUsername) {
      setUsername(storedUsername);
      return;
    }

    const storedToken =
      window.localStorage.getItem("id_token") ||
      window.localStorage.getItem("access_token");

    if (!storedToken) return;

    try {
      const payload = payloadFromToken(storedToken);
      const derivedUsername =
        payload?.["cognito:username"] ||
        payload?.username ||
        payload?.email ||
        payload?.sub;
      if (derivedUsername) {
        setUsername(derivedUsername);
      }
    } catch (err) {
      console.debug("Unable to derive username from token", err);
    }
  }, []);

  const dataWithResolvedOverrides = React.useMemo(
    () =>
      (data || []).map((item) =>
        resolvedOverrides[item.key]
          ? { ...item, summary_resolved: resolvedOverrides[item.key] }
          : item
      ),
    [data, resolvedOverrides],
  );

  const markResolvedAction = React.useCallback(
    async (keysToResolve) => {
      const usernameForRequest = providedUsername || username;
      const sanitizedKeys = (keysToResolve || []).filter(Boolean);
      if (!sanitizedKeys.length) return;
      setIsResolving(true);
      try {
        await resolveCalls({
          keys: sanitizedKeys,
          username: usernameForRequest,
        });
        setResolvedOverrides((previous) => {
          const next = { ...previous };
          sanitizedKeys.forEach((key) => {
            next[key] = usernameForRequest || "Resolved";
          });
          return next;
        });
        setSelectedItems([]);
        onResolved();
      } catch (err) {
        console.error("Failed to mark records resolved", err);
      } finally {
        setIsResolving(false);
      }
    },
    [providedUsername, username, onResolved],
  );
  const qualityScoreSortDirectionRef = React.useRef(true);
  const qualityScoreComparator = React.useCallback((a, b) => {
    const isDescending = qualityScoreSortDirectionRef.current;
    const aValue = getQualityScoreSortValue(a.qualityScore, isDescending);
    const bValue = getQualityScoreSortValue(b.qualityScore, isDescending);

    if (aValue === bValue) return 0;
    return aValue < bValue ? -1 : 1;
  }, []);
  const columnWidthsById = React.useMemo(
    () => {
      const widths = new Map();
      (columnWidths || []).forEach(({ id, width }) => {
        if (id && Number.isFinite(width)) {
          widths.set(id, width);
        }
      });
      return widths;
    },
    [columnWidths],
  );

  const columnDefinitions = React.useMemo(
    () =>
      BASE_COLUMN_DEFINITIONS.map((column) => {
        const baseColumn =
          column.id === 'qualityScore'
            ? { ...column, sortingComparator: qualityScoreComparator }
            : column;
        const savedWidth = columnWidthsById.get(column.id);

        if (Number.isFinite(savedWidth)) {
          return { ...baseColumn, width: savedWidth };
        }
        return baseColumn;
      }),
    [qualityScoreComparator, columnWidthsById],
  );
  const defaultSortingColumn = React.useMemo(
    () => columnDefinitions.find(({ id }) => id === 'timestamp'),
    [columnDefinitions]
  );

  const { items, filteredItemsCount, collectionProps, paginationProps, propertyFilterProps } = useCollection(
    dataWithResolvedOverrides,
    {
      propertyFiltering: {
        filteringProperties: [
          {
            key: "timestamp",
            defaultOperator: '>',
            operators: ['<', '<=', '>', '>='].map(operator => ({
              operator,
              form: DateTimeForm,
              format: formatDateTime,
              match: 'datetime',
            })),
            propertyLabel: "Timestamp",
            groupValuesLabel: "Timestamps"
          },
          {
            key: "jobName",
            operators: ["=", "!=", ":", "!:"],
            propertyLabel: "Job Name",
            groupValuesLabel: "Job Names"
          },
          {
            key: "guid",
            operators: ["=", "!=", ":", "!:"],
            propertyLabel: "GUID",
            groupValuesLabel: "GUIDs"
          },
          {
            key: "agent",
            operators: ["=", "!=", ":", "!:"],
            propertyLabel: "Agent",
            groupValuesLabel: "Agents"
          },
          {
            key: "customer",
            operators: ["=", "!=", ":", "!:"],
            propertyLabel: "Customer",
            groupValuesLabel: "Customers"
          },
          {
            key: "queue",
            operators: ["=", "!=", ":", "!:"],
            propertyLabel: "Queue",
            groupValuesLabel: "Queues"
          },
          {
            key: "state",
            operators: ["=", "!=", ":", "!:"],
            propertyLabel: "State",
            groupValuesLabel: "States"
          },
          {
            key: "summary_resolved",
            operators: ["=", "!=", ":", "!:"],
            propertyLabel: "Resolved",
            groupValuesLabel: "Resolved"
          },
          {
            key: "summary_topic",
            operators: ["=", "!=", ":", "!:"],
            propertyLabel: "Topic",
            groupValuesLabel: "Topics"
          },
          {
            key: "summary_product",
            operators: ["=", "!=", ":", "!:"],
            propertyLabel: "Product",
            groupValuesLabel: "Products"
          },
          {
            key: "lang",
            operators: ["=", "!=", ":", "!:"],
            propertyLabel: "Language Code",
            groupValuesLabel: "Languages Codes"
          },
          {
            key: "duration",
            defaultOperator: '>',
            operators: ['<', '<=', '>', '>='],
            propertyLabel: "Duration",
            groupValuesLabel: "Durations"
          }
        ],
        empty: (
          <div>No Calls.</div>
        ),
        noMatch: (
          <div>No matches.</div>
        )
      },
      pagination: { pageSize: 200 },
      sorting: {
        defaultState: {
          sortingColumn: defaultSortingColumn,
          isDescending: true,
        },
      },
      selection: {},
    });
  const resolvedItems = React.useMemo(
    () => items || [],
    [items],
  );
  const collectionPropsWithSortTracking = React.useMemo(
    () => ({
      ...collectionProps,
      onSortingChange: (event) => {
        qualityScoreSortDirectionRef.current = !!event.detail.isDescending;
        if (collectionProps.onSortingChange) {
          collectionProps.onSortingChange(event);
        }
      },
    }),
    [collectionProps],
  );

  const propertyFilter = (
    <div style={{ display: hideSearchBar ? "none" : "block" }}>
      <PropertyFilter
        {...propertyFilterProps}
        /*onChange={({ detail }) => {
          console.log(detail);
          //setCallQuery(detail);
        }}*/
        //query={callQuery}
        i18nStrings={{
          filteringAriaLabel: "your choice",
          dismissAriaLabel: "Dismiss",
          filteringPlaceholder: "Find calls",
          groupValuesText: "Values",
          groupPropertiesText: "Properties",
          operatorsText: "Operators",
          operationAndText: "and",
          operationOrText: "or",
          operatorLessText: "Less than",
          operatorLessOrEqualText: "Less than or equal",
          operatorGreaterText: "Greater than",
          operatorGreaterOrEqualText:
            "Greater than or equal",
          operatorContainsText: "Contains",
          operatorDoesNotContainText: "Does not contain",
          operatorEqualsText: "Equals",
          operatorDoesNotEqualText: "Does not equal",
          editTokenHeader: "Edit filter",
          propertyText: "Property",
          operatorText: "Operator",
          valueText: "Value",
          cancelActionText: "Cancel",
          applyActionText: "Apply",
          allPropertiesLabel: "All properties",
          tokenLimitShowMore: "Show more",
          tokenLimitShowFewer: "Show fewer",
          clearFiltersText: "Clear filters",
          removeTokenButtonAriaLabel: token =>
            `Remove token ${token.propertyKey} ${token.operator} ${token.value}`,
          enteredTextLabel: text => `Use: "${text}"`
        }}
        countText={getMatchesCountText(filteredItemsCount)}
        expandToViewport={true}
      />
    </div>
  );

  const filter = extraFilters ? (
    <SpaceBetween size="s" direction="vertical">
      {extraFilters}
      {propertyFilter}
    </SpaceBetween>
  ) : (
    propertyFilter
  );

  return (
    <Table
      {...collectionPropsWithSortTracking}
      header={
        (() => {
          const markResolvedButton = (
            <Button
              onClick={() =>
                markResolvedAction(
                  selectedItems.map((item) => item.key).filter(Boolean)
                )
              }
              disabled={!selectedItems.length || isResolving}
              loading={isResolving}
            >
              {`Mark Resolved (${selectedItems.length})`}
            </Button>
          );

          if (React.isValidElement(header)) {
            const existingActions = header.props?.actions;
            const actions = existingActions ? (
              <SpaceBetween size="xs" direction="horizontal">
                {existingActions}
                {markResolvedButton}
              </SpaceBetween>
            ) : (
              markResolvedButton
            );
            return React.cloneElement(header, { actions });
          }

          return (
            <Header actions={markResolvedButton}>
              {header ?? "Calls"}
            </Header>
          );
        })()
      }
      variant={variant}
      columnDefinitions={columnDefinitions}
      columnDisplay={preferences.contentDisplay}
      items={resolvedItems}
      loading={loading}
      pagination={<Pagination {...paginationProps} />}
      resizableColumns={true}
      loadingText="Loading Calls"
      // onSelectionChange={onClick}
      // onRowClick={onClick}
      selectionType="multi"
      trackBy="key"
      selectedItems={selectedItems}
      onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
      stickyHeader={true}
      stickyColumns={{ first: 2, last: 0 }}
      onColumnWidthsChange={({ detail }) => {
        const widths = detail?.widths || [];
        const nextColumnWidths = columnDefinitions.map((column, index) => ({
          id: column.id ?? String(index),
          width: Number.isFinite(widths[index])
            ? widths[index]
            : column.width ?? DEFAULT_COLUMN_WIDTH,
        }));
        setColumnWidths(nextColumnWidths);
      }}
      filter={filter}

      preferences={
        <ContactTablePreferences preferences={preferences} setPreferences={setPreferences} />
      }
      visibleColumns={['jobName', ...preferences.visibleContent]}
    />
  );
};
